import { useMemo } from "react";
import { Avatar, Button, Card, Empty, Table, Tag } from "antd";
import { useTranslation } from "react-i18next";
import { useFormatos } from "../../../../i18n/formatos";
import {
  colorDeInterpretacion,
  textoDeInterpretacion,
} from "../../../../Utils/interpretacionColor";
import "./RecentActivityTable.scss";

// OJO: el color del tag se decide buscando palabras CASTELLANAS dentro de la
// interpretacion que devuelve el servidor. El dia que esa interpretacion llegue
// en otro idioma, todos los tags se pintaran azules. No es texto de interfaz,
// asi que no se traduce aqui, pero queda anotado.
/**
 * ESTA TABLA NUNCA ENSENABA NADA, Y NO ESTABA ROTA.
 *
 * El dashboard abre en "ultimos 30 dias" y, medido contra produccion el
 * 2026-08-11, habia **129 evaluaciones en total y CERO en los ultimos 30
 * dias**: la mas reciente era del 18 de mayo. La tabla estaba contestando bien
 * a la pregunta que se le hacia.
 *
 * Lo que fallaba era lo que ponia al estar vacia: "Sin actividad reciente", sin
 * mencionar el periodo. Eso se lee como "esto no funciona" en vez de como "en
 * esta ventana no hay nada", y son dos conclusiones muy distintas: la primera
 * lleva a quitar la tabla, que es justo lo contrario de lo que hace falta.
 *
 * Ahora el vacio dice EN QUE PERIODO no hay nada y ofrece ampliarlo de un clic.
 * El mismo problema afecta a las dos graficas de arriba y a la puntuacion
 * media, por la misma razon y con la misma solucion.
 */
export function RecentActivityTable({
  loading,
  activity,
  timeRangeLabel,
  onVerTodo,
}) {
  // Dos namespaces: el propio del area y el comun, del que salen las
  // cabeceras "Fecha" y "Paciente".
  // "evaluations" entra por los rotulos de interpretacion, que se traducen
  // desde el codigo que emite el servidor.
  const { t } = useTranslation(["motricity", "common", "evaluations"]);
  const { fechaHora } = useFormatos();
  const dataSource = activity || [];

  const columns = useMemo(
    () => [
      {
        title: t("common:fields.date"),
        dataIndex: "date",
        key: "date",
        width: 160,
        // El formato sale del idioma de la interfaz, no de una plantilla fija.
        render: (v) => fechaHora(v),
      },
      {
        title: t("common:roles.patient"),
        key: "patient",
        width: 200,
        render: (_, row) => (
          <div className="revi-activity-patient">
            <Avatar src={row.patientAvatar} size={32}>
              {(row.patient || "P")[0]?.toUpperCase()}
            </Avatar>
            <span className="revi-activity-patient__name">
              {row.patient || "-"}
            </span>
          </div>
        ),
      },
      {
        title: t("activity.columns.evaluation"),
        dataIndex: "evaluation",
        key: "evaluation",
        ellipsis: true,
      },
      {
        title: t("activity.columns.score"),
        dataIndex: "score",
        key: "score",
        width: 90,
        align: "center",
        render: (v) => (
          <Tag color="blue" style={{ fontWeight: 600 }}>
            {String(v ?? "N/A")}
          </Tag>
        ),
      },
      {
        title: t("activity.columns.interpretation"),
        dataIndex: "interpretation",
        key: "interpretation",
        width: 160,
        // El codigo manda sobre el texto: es identidad estable y se traduce.
        // Los resultados anteriores a que existiera no lo traen, y para esos
        // se sigue usando el texto en castellano, que es como se calcularon.
        render: (v, r) => (
          <Tag
            color={colorDeInterpretacion(v, r?.interpretationCode)}
            bordered={false}
          >
            {textoDeInterpretacion(v, r?.interpretationCode, t)}
          </Tag>
        ),
      },
    ],
    [t, fechaHora],
  );

  return (
    <Card
      className="revi-activity-card"
      variant="outlined"
      title={t("activity.title")}
      loading={loading}
    >
      {!loading && dataSource.length === 0 ? (
        <Empty
          description={
            <>
              {/* Nombrar el periodo es lo que convierte "esto no va" en "aqui
                  no hay nada, mira mas atras". */}
              <div>
                {timeRangeLabel
                  ? t("activity.emptyEnPeriodo", { periodo: timeRangeLabel })
                  : t("activity.empty")}
              </div>
              {onVerTodo ? (
                <Button type="link" size="small" onClick={onVerTodo}>
                  {t("activity.verTodo")}
                </Button>
              ) : null}
            </>
          }
        />
      ) : (
        <Table
          rowKey={(r) => r.id}
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          size="middle"
          scroll={{ x: 700 }}
        />
      )}
    </Card>
  );
}
